import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Section } from '@packages/ui/layout';
import { EmptyState } from '@packages/ui/layout/EmptyState';
import { FormRenderer } from '@packages/form-engine/FormRenderer';
import { useFormRendererRef } from '@packages/form-engine/hooks/useFormRendererRef';
import { loadDomainSchema } from '@packages/form-engine/resolvers/FormLoader';
import { createDefaultContext } from '@packages/form-engine/resolvers/createDefaultContext';
import type { DomainSchemaFiles } from '@packages/form-engine/resolvers/types';
import {
  resolveFormBlockMode,
  type FormBlock,
  type BlockRendererSharedProps,
  type WizardFormHandle,
} from '../types/blocks';
import { getFormLoader } from '../registry/loaderRegistry';
import { BlockLoadState } from './BlockLoadState';
import { useAsyncBlockResource } from './useAsyncBlockResource';
import { useFormSchemaVersion } from './useSchemaVersion';
import { useStepChannel } from '../wizard/v2/hooks/useStepChannel';
import { createFormAdapter } from '../wizard/v2/adapters/FormAdapter';
import { ActionRegistry } from '@packages/table-builder/registry';
import { useStudioPreview } from '../core/StudioPreviewContext';
import { isStudioPreviewEntityId, resolveStudioBlockEntityId } from '../core/studioPreviewEntity';
import { useEventScope } from '@packages/block-events/react/useEventScope';
import { BlockEventType } from '@packages/block-events/core/constants';

export interface FormBlockRendererProps extends BlockRendererSharedProps {
  block: FormBlock;
}

export function FormBlockRenderer({
  block,
  params: paramsProp,
  meta,
  onSuccess,
  onError,
  onCancel,
  handle: controlHandle,
}: FormBlockRendererProps): JSX.Element {
  const channel = useStepChannel();
  const isInsideWizard = channel !== null;
  const scope = useEventScope();

  const formHandle = useFormRendererRef({ hideSubmitBar: isInsideWizard || controlHandle != null || block.hideSubmitBar === true });

  const wrappedOnSuccess = useCallback(
    (payload?: unknown) => {
      onSuccess?.(payload);
      if (isInsideWizard && scope && block.semanticId) {
        scope.bus.publish(BlockEventType.WIZARD_STEP_COMPLETE, block.semanticId, {
          stepId: block.semanticId,
          stepIndex: -1,
          values: formHandle.getFormValues(),
        });
      }
    },
    [onSuccess, isInsideWizard, scope, block.semanticId, formHandle],
  );

  const dirtyCallbackRef = useRef<((isDirty: boolean) => void) | null>(null);
  const blockDirtyRef = useRef(block.onDirtyChange);
  blockDirtyRef.current = block.onDirtyChange;

  useEffect(() => {
    if (!channel || channel.status === 'disposed') {
      dirtyCallbackRef.current = (isDirty) => {
        blockDirtyRef.current?.(isDirty);
      };
      return () => { dirtyCallbackRef.current = null; };
    }

    const handle: WizardFormHandle = {
      validate: () => formHandle.triggerValidation(),
      getValues: () => formHandle.getFormValues(),
      submit: () => formHandle.submitForm(),
      isDirty: () => formHandle.isDirty,
      reset: (values) => formHandle.reset?.(values),
      onDirtyChange: (isDirty) => {
        dirtyCallbackRef.current?.(isDirty);
      },
    };

    const adapter = createFormAdapter(handle, channel);
    dirtyCallbackRef.current = (isDirty) => {
      if (channel.status !== 'disposed') {
        channel.emit({ type: 'DIRTY_CHANGED', isDirty });
      }
      blockDirtyRef.current?.(isDirty);
    };
    channel.connect(adapter);

    return () => {
      dirtyCallbackRef.current = null;
      adapter.destroy();
    };
  }, [channel, formHandle]);

  useEffect(() => {
    dirtyCallbackRef.current?.(formHandle.isDirty);
  }, [formHandle.isDirty]);

  // Surface form state to the parent ControlRenderer handle (e.g. ComposeModal).
  useEffect(() => {
    if (controlHandle) {
      controlHandle._setFormState(formHandle.formId, formHandle.isDirty, formHandle.isSubmitting);
    }
  }, [controlHandle, formHandle.formId, formHandle.isDirty, formHandle.isSubmitting]);

  const loader = useCallback(async () => {
    const formLoader = getFormLoader();
    if (!formLoader) {
      throw new Error(
        `[FormBlockRenderer] No form loader registered. ` +
        `Ensure "@packages/domain" is imported before rendering.`,
      );
    }

    const domainMatch = block.$id.match(/^wealthlane:\/\/forms\/([^/#]+)/);
    if (domainMatch) {
      await ActionRegistry.ensureDomain(domainMatch[1]);
    }

    const schema = await formLoader(block.$id);
    if (!schema) {
      throw new Error(
        `[FormBlockRenderer] No form schema found for block "${block.$id}". ` +
        `Check that a matching *.model.json with $id exists in @packages/domain/modules/.`,
      );
    }

    const files: DomainSchemaFiles = {
      domain: schema.domain as DomainSchemaFiles['domain'],
      orch: schema.orch as DomainSchemaFiles['orch'],
      rules: schema.rules as DomainSchemaFiles['rules'],
    };

    const result = loadDomainSchema(files, createDefaultContext());
    return { ...result, rawDomain: schema.domain as Record<string, unknown> };
  }, [block.$id]);

  const formVersion = useFormSchemaVersion();
  const isUnconfigured = !block.$id || block.$id === '';

  const resource = useAsyncBlockResource(
    isUnconfigured ? '__skip__' : `form-block:${block.$id}`,
    loader,
    formVersion,
  );

  if (isUnconfigured) {
    return (
      <EmptyState
        icon='edit_note'
        title='Form block not configured'
        description='Select this block in the inspector and choose a form schema.'
        semanticId='formBlockUnconfigured'
      />
    );
  }

  const { isStudioPreview, previewDataMode, mockDataBag, resolveMockData } = useStudioPreview();
  const error = resource.status === 'error' ? resource.error : null;
  const resolvedEntityId = resolveStudioBlockEntityId(paramsProp, block.entityId, isStudioPreview);
  const mergedParams = resolvedEntityId ? { ...paramsProp, id: resolvedEntityId } : paramsProp;
  const effectiveMode = resolveFormBlockMode(block.mode, mergedParams, resolvedEntityId);
  const hasPrefetchedData = block.data != null;
  const rawDomain = resource.status === 'ready' ? resource.data.rawDomain : undefined;
  const useStudioMockData = isStudioPreview
    && !isStudioPreviewEntityId(resolvedEntityId)
    && !hasPrefetchedData;

  const studioMockValues = useMemo(() => {
    if (!useStudioMockData || previewDataMode !== 'mock') return undefined;
    if (mockDataBag && Object.keys(mockDataBag).length > 0) return mockDataBag;
    if (resolveMockData && rawDomain) {
      return resolveMockData(block.$id, rawDomain);
    }
    return {};
  }, [useStudioMockData, previewDataMode, mockDataBag, resolveMockData, rawDomain, block.$id]);

  if (error) {
    return <BlockLoadState isLoading={false} error={error} />;
  }

  if (resource.status !== 'ready') {
    return null;
  }

  return (
    <Section title={block.hideTitle ? undefined : block.title} semanticId={block.semanticId}>
      <FormRenderer
        schema={resource.data.schema}
        mode={effectiveMode}
        params={mergedParams}
        meta={meta}
        onSuccess={wrappedOnSuccess}
        onError={onError}
        onCancel={isInsideWizard ? undefined : onCancel}
        formHandle={formHandle}
        semanticId={block.semanticId}
        initialValues={hasPrefetchedData ? block.data : studioMockValues}
        hideTitle={block.hideTitle}
      />
    </Section>
  );
}
